const { BaseDAO } = require('./BaseDAO.js');

class TagDAO extends BaseDAO {
  constructor(blogDAO) {
    super();
    this.blogDAO = blogDAO;
    this.cacheTTL = 10 * 60 * 1000; // 10 minutes
  }

  // Get all tags from blog database
  async getAllTags() {
    const cacheKey = 'all_tags';
    const cached = this.getCached(cacheKey);
    if (cached) return cached;

    try {
      const blogDb = await this.blogDAO.getBlogDatabase();
      const tags = blogDb.tags || [];
      
      this.setCached(cacheKey, tags);
      return tags;
    } catch (error) {
      console.error('Failed to load tags:', error);
      return [];
    }
  }

  // Convert tag indices to tag names
  async getTagNames(tagIndices) {
    if (!tagIndices || tagIndices.length === 0) return [];
    
    const allTags = await this.getAllTags();
    return tagIndices
      .map(index => allTags[index])
      .filter(Boolean);
  }

  // Convert tag names to indices  
  async getTagIndices(tagNames) {
    if (!tagNames || tagNames.length === 0) return [];
    
    const allTags = await this.getAllTags();
    return tagNames
      .map(name => allTags.indexOf(name))
      .filter(index => index !== -1);
  }

  // Get tag statistics (usage count)
  async getTagStats() {
    const cacheKey = 'tag_stats';
    const cached = this.getCached(cacheKey);
    if (cached) return cached;

    try {
      const blogDb = await this.blogDAO.getBlogDatabase();
      const allTags = blogDb.tags || [];
      const tagUsage = {};

      // Initialize all tags with 0 count
      allTags.forEach((tag, index) => {
        tagUsage[index] = { name: tag, count: 0, index };
      });

      // Count tag usage in stories
      (blogDb.stories || []).forEach(story => {
        if (story.tags && Array.isArray(story.tags)) {
          story.tags.forEach(tagIndex => {
            if (tagUsage[tagIndex]) {
              tagUsage[tagIndex].count++;
            }
          });
        }
      });

      const stats = Object.values(tagUsage).sort((a, b) => b.count - a.count);
      
      this.setCached(cacheKey, stats);
      return stats;
    } catch (error) {
      console.error('Failed to calculate tag stats:', error);
      return [];
    }
  }

  // Get popular tags (most used)
  async getPopularTags(limit = 20) {
    const cacheKey = `popular_tags_${limit}`;
    const cached = this.getCached(cacheKey);
    if (cached) return cached;

    try {
      const tagStats = await this.getTagStats();
      const popularTags = tagStats
        .filter(tag => tag.count > 0)
        .slice(0, limit);

      this.setCached(cacheKey, popularTags);
      return popularTags;
    } catch (error) {
      console.error('Failed to get popular tags:', error);
      return [];
    }
  }

  // Get related tags (tags that appear together)
  async getRelatedTags(tagName, limit = 10) {
    const cacheKey = `related_tags_${tagName}_${limit}`;
    const cached = this.getCached(cacheKey);
    if (cached) return cached;

    try {
      const blogDb = await this.blogDAO.getBlogDatabase();
      const allTags = blogDb.tags || [];
      const targetTagIndex = allTags.indexOf(tagName);
      
      if (targetTagIndex === -1) return [];

      const coOccurrence = {};
      
      // Find stories with target tag and count co-occurring tags
      (blogDb.stories || []).forEach(story => {
        if (story.tags && story.tags.includes(targetTagIndex)) {
          story.tags.forEach(tagIndex => {
            if (tagIndex !== targetTagIndex && allTags[tagIndex]) {
              const tagName = allTags[tagIndex];
              coOccurrence[tagName] = (coOccurrence[tagName] || 0) + 1;
            }
          });
        }
      });

      const relatedTags = Object.entries(coOccurrence)
        .map(([name, count]) => ({ name, count }))
        .sort((a, b) => b.count - a.count)
        .slice(0, limit);

      this.setCached(cacheKey, relatedTags);
      return relatedTags;
    } catch (error) {
      console.error(`Failed to get related tags for ${tagName}:`, error);
      return [];
    }
  }

  // Search tags by name
  async searchTags(query, limit = 20) {
    if (!query || query.trim().length < 1) return [];

    const cacheKey = `search_tags_${query}_${limit}`;
    const cached = this.getCached(cacheKey);
    if (cached) return cached;

    try {
      const allTags = await this.getAllTags();
      const searchTerm = query.toLowerCase();
      
      const matchingTags = allTags
        .map((tag, index) => ({ name: tag, index }))
        .filter(tag => tag.name.toLowerCase().includes(searchTerm))
        .slice(0, limit);

      this.setCached(cacheKey, matchingTags);
      return matchingTags;
    } catch (error) {
      console.error(`Failed to search tags for ${query}:`, error);
      return [];
    }
  }

  // Get tag cloud data (tags with weights)
  async getTagCloud(minCount = 1, maxTags = 50) {
    const cacheKey = `tag_cloud_${minCount}_${maxTags}`;
    const cached = this.getCached(cacheKey);
    if (cached) return cached;

    try {
      const tagStats = await this.getTagStats();
      
      const filteredTags = tagStats
        .filter(tag => tag.count >= minCount)
        .slice(0, maxTags);

      if (filteredTags.length === 0) return [];

      const maxCount = Math.max(...filteredTags.map(t => t.count));
      const minCountValue = Math.min(...filteredTags.map(t => t.count));
      
      const tagCloud = filteredTags.map(tag => ({
        name: tag.name,
        count: tag.count,
        weight: minCountValue === maxCount ? 1 : 
                (tag.count - minCountValue) / (maxCount - minCountValue),
        fontSize: Math.max(0.8, Math.min(2, 0.8 + (tag.count / maxCount) * 1.2))
      }));

      this.setCached(cacheKey, tagCloud);
      return tagCloud;
    } catch (error) {
      console.error('Failed to generate tag cloud:', error);
      return [];
    }
  }

  // Get tags by category (analyze tag patterns in categories)
  async getTagsByCategory() {
    const cacheKey = 'tags_by_category';
    const cached = this.getCached(cacheKey);
    if (cached) return cached;

    try {
      const blogDb = await this.blogDAO.getBlogDatabase();
      const allTags = blogDb.tags || [];
      const categoryTags = {};

      (blogDb.stories || []).forEach(story => {
        const category = story.category;
        if (!category || !story.tags) return;

        if (!categoryTags[category]) {
          categoryTags[category] = {};
        }

        story.tags.forEach(tagIndex => {
          const tagName = allTags[tagIndex];
          if (tagName) {
            categoryTags[category][tagName] = (categoryTags[category][tagName] || 0) + 1;
          }
        });
      });

      // Convert to array format with sorted tags per category
      const result = Object.entries(categoryTags).map(([category, tags]) => ({
        category,
        tags: Object.entries(tags)
          .map(([name, count]) => ({ name, count }))
          .sort((a, b) => b.count - a.count)
      }));

      this.setCached(cacheKey, result);
      return result;
    } catch (error) {
      console.error('Failed to get tags by category:', error);
      return [];
    }
  }

  // Calculate tag overlap between two sets of tags
  calculateTagOverlap(tags1, tags2) {
    if (!tags1 || !tags2 || tags1.length === 0 || tags2.length === 0) {
      return 0;
    }
    
    const set1 = new Set(tags1);
    const overlap = tags2.filter(tag => set1.has(tag));
    return overlap.length;
  }

  // Get stories by tag index
  async getStoriesByTag(tagIndex) {
    const cacheKey = `stories_by_tag_${tagIndex}`;
    const cached = this.getCached(cacheKey);
    if (cached) return cached;

    try {
      const blogDb = await this.blogDAO.getBlogDatabase();
      const stories = (blogDb.stories || [])
        .filter(story => story.tags && story.tags.includes(tagIndex))
        .map(story => ({
          id: story.id,
          title: story.title,
          category: story.category,
          publishDate: story.publish_date || story.publishDate,
          authorSlug: story.author_slug,
          featured: story.featured
        }));

      this.setCached(cacheKey, stories);
      return stories;
    } catch (error) {
      console.error(`Failed to get stories by tag ${tagIndex}:`, error);
      return [];
    }
  }

  // Get stories by tag name
  async getStoriesByTagName(tagName) {
    try {
      const allTags = await this.getAllTags();
      const tagIndex = allTags.indexOf(tagName);
      
      if (tagIndex === -1) return [];
      
      return await this.getStoriesByTag(tagIndex);
    } catch (error) {
      console.error(`Failed to get stories by tag name ${tagName}:`, error);
      return [];
    }
  }

  // Validate tag exists
  async tagExists(tagName) {
    try {
      const allTags = await this.getAllTags();
      return allTags.includes(tagName);
    } catch (error) {
      return false;
    }
  }
}

module.exports = { TagDAO };